import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  BanknotesIcon,
  ClipboardDocumentListIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const api = (path) => `${process.env.REACT_APP_API_URL}${path}`;

function formatTransactionDescription(description) {
  return (description || '').replace(/^Dette miellerie/, 'Dette usage de la miellerie');
}

function typeLabel(type) {
  const m = {
    paiement: 'Paiement',
    reservation: 'Dette',
  };
  return m[type] || type;
}

function paymentMethodLabel(methode) {
  const m = {
    espece: 'Espèces',
    cheque: 'Chèque',
    stripe: 'Carte',
    virement: 'Virement',
  };
  return m[methode] || methode;
}

function formatDateFr(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR');
}

export default function SuiviPaiements() {
  const currentYear = new Date().getFullYear();
  const [annee, setAnnee] = useState(currentYear);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  const anneesOptions = useMemo(() => {
    const years = [];
    for (let y = currentYear + 1; y >= currentYear - 5; y -= 1) years.push(y);
    return years;
  }, [currentYear]);

  const fetchPaiements = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const { data: payload } = await axios.get(
        api(`/miellerie/responsables/mes-paiements?annee=${annee}`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData(payload);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors du chargement des paiements');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [annee]);

  useEffect(() => {
    fetchPaiements();
  }, [fetchPaiements]);

  const totaux = data?.totaux;
  const transactions = data?.transactions || [];

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-800 font-medium text-sm">Erreur</p>
            <p className="text-red-700 text-sm">{error}</p>
          </div>
          <button type="button" onClick={() => setError('')} className="text-red-600">
            ×
          </button>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Suivi des paiements</h3>
          <p className="text-sm text-slate-500 mt-0.5">
            Encaissements et dettes enregistrés sur vos plages d&apos;ouverture
          </p>
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-slate-600">
          Année
          <select
            value={annee}
            onChange={(e) => setAnnee(parseInt(e.target.value, 10))}
            className="px-2.5 py-1.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-honey-500 focus:border-honey-500"
          >
            {anneesOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>
      </div>

      {totaux && (
        <div className="flex flex-wrap gap-2">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-medium">
            {totaux.nombreTransactions} mouvement
            {totaux.nombreTransactions !== 1 ? 's' : ''}
          </span>
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-100 text-green-800 text-xs font-medium">
            Encaissements {Number(totaux.totalEncaissements || 0).toFixed(2)} €
          </span>
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-100 text-red-800 text-xs font-medium">
            Dettes {Number(totaux.totalDettes || 0).toFixed(2)} €
          </span>
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-honey-100 text-honey-900 text-xs font-semibold">
            <BanknotesIcon className="h-3.5 w-3.5" />
            Total {Number(totaux.total || 0).toFixed(2)} €
          </span>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-honey-600" />
        </div>
      ) : transactions.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 p-10 text-center">
          <ClipboardDocumentListIcon className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Aucun paiement pour l&apos;année {annee}</p>
          <p className="text-slate-400 text-sm mt-1">
            Les encaissements et dettes apparaissent après validation d&apos;un passage sur vos plages.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg bg-white shadow">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Usager
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                  Détail
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Montant
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {transactions.map((t) => {
                const nomUsager = t.user
                  ? `${t.user.prenom || ''} ${t.user.nom || ''}`.trim()
                  : '—';
                return (
                  <tr key={t._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                      {formatDateFr(t.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                      {nomUsager || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                            t.type === 'paiement'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {typeLabel(t.type)}
                        </span>
                        {t.paiement?.methode && (
                          <span className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded">
                            {paymentMethodLabel(t.paiement.methode)}
                          </span>
                        )}
                        {t.paiement?.methode === 'cheque' && (
                          <span
                            className={`text-xs px-2 py-0.5 rounded ${
                              t.paiement?.chequeStatut === 'encaisse'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-amber-100 text-amber-900'
                            }`}
                          >
                            {t.paiement?.chequeStatut === 'encaisse' ? 'Encaissé' : 'Non encaissé'}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 hidden sm:table-cell">
                      <p className="text-sm text-slate-700">
                        {formatTransactionDescription(t.description)}
                      </p>
                      {t.effectuePar && (
                        <p className="mt-0.5 text-xs text-slate-400">
                          Validé par {t.effectuePar.prenom} {t.effectuePar.nom} le{' '}
                          {formatDateFr(t.createdAt)}
                        </p>
                      )}
                    </td>
                    <td
                      className={`px-6 py-4 whitespace-nowrap text-right text-sm font-semibold tabular-nums ${
                        t.montant >= 0 ? 'text-green-700' : 'text-red-700'
                      }`}
                    >
                      {t.montant > 0 ? '+' : ''}
                      {Number(t.montant || 0).toFixed(2)} €
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
